export type CanvasAxis = 'x' | 'y';
export type DistributionAxis = 'horizontal' | 'vertical';
export type HorizontalAlignment = 'left' | 'center' | 'right';
export type VerticalAlignment = 'top' | 'middle' | 'bottom';
export type CanvasAlignment = HorizontalAlignment | VerticalAlignment;
export type HorizontalAnchor = HorizontalAlignment;
export type VerticalAnchor = 'top' | 'middle' | 'bottom';

export interface CanvasDimensions {
  width: number;
  height: number;
}

export interface CanvasElementGeometry {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
}

export interface SmartGuide {
  axis: CanvasAxis;
  position: number;
  source: 'page' | 'element';
  movingAnchor: HorizontalAnchor | VerticalAnchor;
  targetAnchor: HorizontalAnchor | VerticalAnchor;
  targetElementId?: string;
  label: string;
}

export interface SnapResult {
  x: number;
  y: number;
  guides: SmartGuide[];
}

interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
  center: number;
  middle: number;
}

interface SnapTarget {
  position: number;
  anchor: HorizontalAnchor | VerticalAnchor;
  source: SmartGuide['source'];
  targetElementId?: string;
  priority: number;
}

interface SnapCandidate {
  adjustment: number;
  guide: SmartGuide;
  priority: number;
  movingPriority: number;
}

const BOUNDS_EPSILON = 0.0001;

/** Axis-aligned painted bounds for an element rotated around its center. */
export const getCanvasElementBounds = (element: CanvasElementGeometry): Bounds => {
  const radians = ((element.rotation || 0) * Math.PI) / 180;
  const rotatedWidth = element.width * Math.abs(Math.cos(radians))
    + element.height * Math.abs(Math.sin(radians));
  const rotatedHeight = element.width * Math.abs(Math.sin(radians))
    + element.height * Math.abs(Math.cos(radians));
  const center = element.x + element.width / 2;
  const middle = element.y + element.height / 2;
  return {
    left: center - rotatedWidth / 2,
    top: middle - rotatedHeight / 2,
    right: center + rotatedWidth / 2,
    bottom: middle + rotatedHeight / 2,
    center,
    middle
  };
};

const horizontalAnchors = (bounds: Bounds): Array<[HorizontalAnchor, number]> => [
  ['left', bounds.left],
  ['center', bounds.center],
  ['right', bounds.right]
];

const verticalAnchors = (bounds: Bounds): Array<[VerticalAnchor, number]> => [
  ['top', bounds.top],
  ['middle', bounds.middle],
  ['bottom', bounds.bottom]
];

const compatibleAnchors = (
  axis: CanvasAxis,
  moving: HorizontalAnchor | VerticalAnchor,
  target: HorizontalAnchor | VerticalAnchor,
  source: SmartGuide['source']
) => {
  if (moving === target) return true;
  if (source === 'page') return false;
  return axis === 'x'
    ? (moving === 'left' && target === 'right') || (moving === 'right' && target === 'left')
    : (moving === 'top' && target === 'bottom') || (moving === 'bottom' && target === 'top');
};

const targetLabel = (target: SnapTarget) => target.source === 'page'
  ? `page ${target.anchor}`
  : `element ${target.targetElementId} ${target.anchor}`;

const bestCandidate = (
  axis: CanvasAxis,
  movingAnchors: Array<[HorizontalAnchor | VerticalAnchor, number]>,
  targets: SnapTarget[],
  threshold: number
): SnapCandidate | null => {
  const candidates: SnapCandidate[] = [];
  targets.forEach(target => movingAnchors.forEach(([movingAnchor, movingPosition], movingPriority) => {
    if (!compatibleAnchors(axis, movingAnchor, target.anchor, target.source)) return;
    const adjustment = target.position - movingPosition;
    if (Math.abs(adjustment) > threshold + BOUNDS_EPSILON) return;
    candidates.push({
      adjustment,
      priority: target.priority,
      movingPriority,
      guide: {
        axis,
        position: target.position,
        source: target.source,
        movingAnchor,
        targetAnchor: target.anchor,
        targetElementId: target.targetElementId,
        label: `${movingAnchor} aligned to ${targetLabel(target)}`
      }
    });
  }));

  candidates.sort((first, second) => (
    Math.abs(first.adjustment) - Math.abs(second.adjustment)
    || first.priority - second.priority
    || first.movingPriority - second.movingPriority
    || first.adjustment - second.adjustment
  ));
  return candidates[0] || null;
};

const pageTargets = (canvas: CanvasDimensions, axis: CanvasAxis): SnapTarget[] => (
  axis === 'x'
    ? [
      { position: 0, anchor: 'left', source: 'page', priority: 0 },
      { position: canvas.width / 2, anchor: 'center', source: 'page', priority: 1 },
      { position: canvas.width, anchor: 'right', source: 'page', priority: 2 }
    ]
    : [
      { position: 0, anchor: 'top', source: 'page', priority: 0 },
      { position: canvas.height / 2, anchor: 'middle', source: 'page', priority: 1 },
      { position: canvas.height, anchor: 'bottom', source: 'page', priority: 2 }
    ]
);

const elementTargets = (
  elements: CanvasElementGeometry[],
  axis: CanvasAxis,
  priorityOffset: number
): SnapTarget[] => [...elements]
  .sort((first, second) => first.id.localeCompare(second.id))
  .flatMap((element, elementIndex) => {
    const bounds = getCanvasElementBounds(element);
    const anchors = axis === 'x' ? horizontalAnchors(bounds) : verticalAnchors(bounds);
    return anchors.map(([anchor, position], anchorIndex) => ({
      position,
      anchor,
      source: 'element' as const,
      targetElementId: element.id,
      priority: priorityOffset + elementIndex * 3 + anchorIndex
    }));
  });

/**
 * Snaps a proposed move to the nearest compatible page or peer-element line.
 * Inputs and output use unscaled canvas coordinates; callers convert the
 * screen-pixel threshold through their current canvas scale.
 */
export const snapCanvasElement = (
  element: CanvasElementGeometry,
  proposedPosition: Pick<CanvasElementGeometry, 'x' | 'y'>,
  canvas: CanvasDimensions,
  peers: CanvasElementGeometry[],
  threshold: number
): SnapResult => {
  const proposed = { ...element, ...proposedPosition };
  const bounds = getCanvasElementBounds(proposed);
  const eligiblePeers = peers.filter(peer => peer.id !== element.id);
  const horizontal = bestCandidate(
    'x',
    horizontalAnchors(bounds),
    [...pageTargets(canvas, 'x'), ...elementTargets(eligiblePeers, 'x', 3)],
    Math.max(0, threshold)
  );
  const vertical = bestCandidate(
    'y',
    verticalAnchors(bounds),
    [...pageTargets(canvas, 'y'), ...elementTargets(eligiblePeers, 'y', 3)],
    Math.max(0, threshold)
  );
  return {
    x: proposed.x + (horizontal?.adjustment || 0),
    y: proposed.y + (vertical?.adjustment || 0),
    guides: [horizontal?.guide, vertical?.guide].filter((guide): guide is SmartGuide => Boolean(guide))
  };
};

/** Aligns the selected element's rotation-aware bounds to the page. */
export const alignCanvasElement = (
  element: CanvasElementGeometry,
  canvas: CanvasDimensions,
  alignment: CanvasAlignment
): Pick<CanvasElementGeometry, 'x' | 'y'> => {
  const bounds = getCanvasElementBounds(element);
  let deltaX = 0;
  let deltaY = 0;
  if (alignment === 'left') deltaX = -bounds.left;
  else if (alignment === 'center') deltaX = canvas.width / 2 - bounds.center;
  else if (alignment === 'right') deltaX = canvas.width - bounds.right;
  else if (alignment === 'top') deltaY = -bounds.top;
  else if (alignment === 'middle') deltaY = canvas.height / 2 - bounds.middle;
  else if (alignment === 'bottom') deltaY = canvas.height - bounds.bottom;
  return { x: element.x + deltaX, y: element.y + deltaY };
};

export const nudgeCanvasElement = (
  element: CanvasElementGeometry,
  direction: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown',
  step: number
): Pick<CanvasElementGeometry, 'x' | 'y'> => {
  const distance = Math.max(0, step);
  return {
    x: element.x + (direction === 'ArrowLeft' ? -distance : direction === 'ArrowRight' ? distance : 0),
    y: element.y + (direction === 'ArrowUp' ? -distance : direction === 'ArrowDown' ? distance : 0)
  };
};

/**
 * Evenly spaces element centers along one axis while preserving the first and
 * last spatial anchor centers. Ties retain their original input order, and the
 * returned array always retains the caller's original ordering.
 */
export const distributeCanvasElements = <T extends CanvasElementGeometry>(
  elements: T[],
  axis: DistributionAxis
): T[] => {
  if (elements.length < 3) return elements.map(element => ({ ...element }));
  const centerFor = (element: CanvasElementGeometry) => axis === 'horizontal'
    ? element.x + element.width / 2
    : element.y + element.height / 2;
  const ordered = elements
    .map((element, originalIndex) => ({ element, originalIndex, center: centerFor(element) }))
    .sort((first, second) => first.center - second.center || first.originalIndex - second.originalIndex);
  const firstCenter = ordered[0].center;
  const lastCenter = ordered[ordered.length - 1].center;
  const spacing = (lastCenter - firstCenter) / (ordered.length - 1);
  const updates = new Map<number, { x: number; y: number }>();
  ordered.forEach((current, spatialIndex) => {
    const desiredCenter = firstCenter + spacing * spatialIndex;
    updates.set(current.originalIndex, axis === 'horizontal'
      ? { x: current.element.x + desiredCenter - current.center, y: current.element.y }
      : { x: current.element.x, y: current.element.y + desiredCenter - current.center });
  });
  return elements.map((element, originalIndex) => ({
    ...element,
    ...updates.get(originalIndex)!
  }));
};

export const getImageAccessibleName = (image: {
  altText?: string;
  characterName?: string;
  prompt?: string;
  layoutRole?: string;
}) => {
  const altText = String(image.altText || '').replace(/\s+/g, ' ').trim();
  if (altText) return altText;
  const characterName = String(image.characterName || '').trim();
  if (characterName) return `Character illustration: ${characterName}`;
  const prompt = String(image.prompt || '').replace(/\s+/g, ' ').trim();
  if (prompt) return `Storybook illustration: ${prompt.slice(0, 120)}`;
  if (image.layoutRole === 'background') return 'Background illustration';
  if (image.layoutRole === 'decoration') return 'Decorative illustration';
  return 'Storybook illustration';
};
